import { Drawer } from "@mui/material";
import { memo, useEffect, useRef, useState } from "react";
import { getManagers, validateManagerPin } from "../../../../../../config/Services";
import { useSnackbar } from "../../../hooks/SnackbarProvider";
/* Reuse the existing Payment Mode drawer CSS — classes are globally injected */
import "../twoWaykeyboardPaymentPage/components/managerAuth/index.css";

/**
 * ManagerPinVerifyModal
 *
 * A generic manager-PIN gate that calls `validateManagerPin` and fires
 * `onSuccess()` on approval.  It does NOT touch cartStoreMode or any other
 * global state — the caller decides what to do after authorisation.
 *
 * Props:
 *   open        {boolean}   — controls drawer visibility
 *   onClose     {function}  — called when the user cancels / dismisses
 *   onSuccess   {function}  — called after the PIN is verified successfully
 *   title       {string}    — drawer header text (optional)
 */
const ManagerPinVerifyModal = ({ open, onClose, onSuccess, title = "Manager Authorisation" }) => {
  const showSnackbar = useSnackbar();
  const [loader, setLoader] = useState(false);
  const [managerList, setManagerList] = useState([]);
  const [selectedManager, setSelectedManager] = useState(null);
  const [pinValue, setPinValue] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const locId = localStorage.getItem("locId");

  /* ── Reset state whenever the drawer opens/closes ── */
  useEffect(() => {
    if (!open) {
      setSelectedManager(null);
      setPinValue("");
      setDropdownOpen(false);
    }
  }, [open]);

  /* ── Fetch manager list when drawer opens ── */
  useEffect(() => {
    if (open) {
      getManagers({ contextId: locId }).then((res) => {
        const list = res?.data?.data?.map((e) => ({
          value: e?.mobile_number,
          label: e?.first_name,
          uidx: e?.uidx,
        }));
        setManagerList(list || []);
      });
    }
  }, [open]);

  /* ── Close dropdown on outside click ── */
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    if (dropdownOpen) {
      document.addEventListener("mousedown", handleOutsideClick);
    }
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [dropdownOpen]);

  /* ── Enter key shortcut ── */
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e) => {
      if (e.key === "Enter" && selectedManager && pinValue.length >= 4) {
        handleVerify();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selectedManager, pinValue]);

  const handleVerify = () => {
    if (!selectedManager || pinValue.length < 4) return;
    setLoader(true);
    validateManagerPin({ mobile: selectedManager.value, primaryPassword: pinValue })
      .then(() => {
        showSnackbar("Manager authorised successfully", "success");
        onSuccess();
      })
      .catch((err) => {
        showSnackbar(
          err?.response?.data?.message || err?.message || "Invalid manager PIN",
          "error"
        );
      })
      .finally(() => {
        setLoader(false);
        setPinValue("");
      });
  };

  const handleClose = () => {
    if (loader) return;
    onClose();
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      PaperProps={{ className: "pm-drawer-paper" }}
    >
      <div className="pm-drawer-root">
        {/* ── Header ── */}
        <div className="pm-drawer-header">
          <p className="pm-drawer-header-title">{title}</p>
          <button className="pm-drawer-close-btn" onClick={handleClose} aria-label="Close">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M18 6 6 18" />
              <path d="m6 6 12 12" />
            </svg>
          </button>
        </div>

        {/* ── Body ── */}
        <div className="pm-drawer-body">
          {/* Info banner */}
          <div className="pm-status-card">
            <div className="pm-status-card-header">
              <p className="pm-status-card-title">Offline Payment</p>
              <span className="pm-status-badge offline">
                <span className="pm-status-badge-dot" />
                Offline Mode
              </span>
            </div>
            <p className="pm-status-card-desc">
              This payment will be recorded in offline mode, bypassing the EDC machine. Manager
              authorisation is required to proceed.
            </p>
          </div>

          {/* PIN auth card */}
          <div className="pm-auth-card">
            <div className="pm-auth-fields-row">
              {/* Manager dropdown */}
              <div className="pm-field-group">
                <p className="pm-field-label">Manager Name</p>
                <div className="pm-custom-select-wrapper" ref={dropdownRef}>
                  <button
                    type="button"
                    className={`pm-custom-select-trigger ${dropdownOpen ? "open" : ""}`}
                    onClick={() => setDropdownOpen((prev) => !prev)}
                  >
                    <span className={selectedManager ? "pm-select-value" : "pm-select-placeholder"}>
                      {selectedManager ? selectedManager.label : "Select manager"}
                    </span>
                    <svg
                      className={`pm-select-chevron ${dropdownOpen ? "rotated" : ""}`}
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="m6 9 6 6 6-6" />
                    </svg>
                  </button>
                  {dropdownOpen && (
                    <div className="pm-custom-select-dropdown">
                      <div
                        className="pm-custom-select-option placeholder-option"
                        onClick={() => setDropdownOpen(false)}
                      >
                        Select manager
                      </div>
                      {managerList?.map((m) => (
                        <div
                          key={m.value}
                          className={`pm-custom-select-option ${
                            selectedManager?.value === m.value ? "pm-selected" : ""
                          }`}
                          onClick={() => {
                            setSelectedManager(m);
                            setDropdownOpen(false);
                          }}
                        >
                          <span className="pm-option-name">{m.label}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* PIN input */}
              <div className="pm-field-group">
                <p className="pm-field-label">Manager PIN</p>
                <input
                  className="pm-pin-input"
                  type="password"
                  inputMode="numeric"
                  placeholder="Enter 4-digit PIN"
                  autoComplete="new-password"
                  maxLength={4}
                  value={pinValue}
                  onChange={(e) => setPinValue(e.target.value)}
                />
              </div>
            </div>

            {/* Action buttons */}
            <div className="pm-auth-actions">
              <button className="pm-auth-btn cancel" onClick={handleClose} disabled={loader}>
                Cancel
              </button>
              <button
                className={`pm-auth-btn confirm ${loader ? "loading" : ""}`}
                disabled={!selectedManager || pinValue.length < 4 || loader}
                onClick={handleVerify}
              >
                {loader ? "Verifying..." : "Authorise"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Drawer>
  );
};

export default memo(ManagerPinVerifyModal);
